import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { articles, type ArticleSummary } from "@/mocks/articles";
import { featuredTemplates } from "@/mocks/templates";
import { categories } from "@/mocks/categories";

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 cursor-pointer">
      <div className="w-12 h-12 flex items-center justify-center rounded-md mb-4" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
        <i className={`${icon} text-xl`} style={{ color: "oklch(var(--accent-500))" }}></i>
      </div>
      <h3 className="text-base font-semibold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed">{description}</p>
    </div>
  );
}

function TemplateCard({ template }: { template: typeof featuredTemplates[0] }) {
  return (
    <div className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 cursor-pointer">
      <div className="relative w-full aspect-[4/3] overflow-hidden">
        <img src={template.image} alt={template.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {template.isNew && (
          <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full text-background-50" style={{ backgroundColor: "oklch(var(--primary-500))" }}>NEW</span>
        )}
        {template.isHot && (
          <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full text-background-50" style={{ backgroundColor: "oklch(var(--accent-500))" }}>HOT</span>
        )}
      </div>
      <div className="p-4">
        <h4 className="text-sm font-medium text-foreground-950 mb-1">{template.title}</h4>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-500">{template.category}</span>
          <span className="text-xs text-foreground-400">
            <i className="ri-download-line mr-0.5"></i>{template.downloads}
          </span>
        </div>
      </div>
    </div>
  );
}

function ArticleCard({ article }: { article: ArticleSummary }) {
  const { t } = useTranslation();
  return (
    <Link to={`/article/${article.id}`} className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 no-underline cursor-pointer">
      <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-md overflow-hidden">
        <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
      </div>
      <div className="flex flex-col justify-between flex-1 min-w-0">
        <div>
          <span className="text-xs text-foreground-500 mb-1.5 inline-block">{article.categoryName}</span>
          <h3 className="text-base font-semibold text-foreground-950 mb-1.5 line-clamp-2 group-hover:text-foreground-800 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>{article.title}</h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-400 mt-3">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.date}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}

const features = [
  { icon: "ri-layout-grid-line", title: "海量模板库", description: "数千款专业设计模板，覆盖社交媒体、电商、企业办公等全场景，一键套用，即改即用。" },
  { icon: "ri-image-edit-line", title: "智能图文编辑", description: "拖拽式编辑器，无需任何设计基础。智能排版、一键配色，让你的创意快速落地。" },
  { icon: "ri-stack-line", title: "丰富素材资源", description: "数百万高清图片、字体、图标、插画素材，全部获得商业授权，放心使用无版权烦恼。" },
  { icon: "ri-team-line", title: "团队协作", description: "支持多人实时协作，统一管理品牌素材，让团队设计效率大幅提升。" },
  { icon: "ri-smartphone-line", title: "多端同步", description: "Web端、手机端数据实时同步，随时随地开始创作，灵感不再错过。" },
  { icon: "ri-shield-check-line", title: "商用保障", description: "所有模板和素材均提供商业使用授权，让你的设计作品安全无忧。" },
];

export default function Homepage() {
  const { t } = useTranslation();

  const latestArticles: ArticleSummary[] = articles.slice(0, 4).map(({ id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime }) => ({
    id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime,
  }));

  const stats = [
    { value: "100万+", label: t("home.stats_users") },
    { value: "5,000+", label: t("home.stats_templates") },
    { value: "1亿+", label: t("home.stats_downloads") },
    { value: "98%", label: t("home.stats_satisfaction") },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "黄油网站",
    "url": "https://huangyou.design",
    "description": t("seo.home_description"),
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://huangyou.design/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "黄油网站",
    "url": "https://huangyou.design",
    "description": t("seo.home_description"),
    "sameAs": [
      "https://weibo.com/huangyou",
      "https://www.xiaohongshu.com/huangyou",
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}></script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[580px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Creative%20minimal%20design%20studio%20workspace%20with%20abstract%20geometric%20shapes%20in%20warm%20cream%20and%20terracotta%20tones%2C%20soft%20natural%20lighting%2C%20artistic%20composition%20with%20floating%20design%20elements%2C%20modern%20scandinavian%20aesthetic%2C%20clean%20elegant%20atmosphere%2C%20high%20end%20editorial%20style&width=1600&height=900&seq=hero-butter-site&orientation=landscape"
              alt="黄油网站创意设计平台"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
              <h1 className="text-2xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                {t("home.hero_title")}
              </h1>
              <p className="text-sm md:text-lg text-white/85 mb-6 md:mb-8 leading-relaxed max-w-2xl mx-auto">
                {t("home.hero_subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button className="text-sm md:text-base text-background-50 px-8 py-3 rounded-md font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90" style={{ backgroundColor: "oklch(var(--accent-500))" }}>
                  {t("home.hero_cta")}
                </button>
                <Link to="/category/muban-sucai" className="text-sm md:text-base text-white/90 px-8 py-3 rounded-md font-medium border border-white/30 cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/10 no-underline">
                  {t("home.hero_secondary")}
                </Link>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1" style={{ fontFamily: "var(--font-heading)" }}>{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="w-full py-14 md:py-20" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>{t("home.features_title")}</h2>
                <p className="text-sm md:text-base text-foreground-500">{t("home.features_subtitle")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {features.map((feature, i) => (
                  <FeatureCard key={i} {...feature} />
                ))}
              </div>
            </div>
          </section>

          {/* Templates */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 md:mb-14 gap-3">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{t("home.template_title")}</h2>
                  <p className="text-sm text-foreground-500">{t("home.template_subtitle")}</p>
                </div>
                <Link to="/category/muban-sucai" className="text-sm font-medium whitespace-nowrap cursor-pointer no-underline flex items-center gap-1 transition-colors" style={{ color: "oklch(var(--accent-500))" }}>
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                {featuredTemplates.slice(0, 8).map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            </div>
          </section>

          {/* Articles */}
          <section className="w-full py-14 md:py-20" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 md:mb-14 gap-3">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{t("home.article_title")}</h2>
                  <p className="text-sm text-foreground-500">{t("home.article_subtitle")}</p>
                </div>
                <Link to="/category/sheji-linggan" className="text-sm font-medium whitespace-nowrap cursor-pointer no-underline flex items-center gap-1 transition-colors" style={{ color: "oklch(var(--accent-500))" }}>
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="flex flex-col gap-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Categories */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>探索创作分类</h2>
                <p className="text-sm md:text-base text-foreground-500">{t("category.subtitle")}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2.5 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-md" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className={`${cat.icon} text-lg`} style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden" style={{ backgroundColor: "oklch(var(--secondary-100))" }}>
            <div className="absolute inset-0 opacity-5">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20soft%20organic%20shapes%20in%20monochrome%20cream%20tones%2C%20minimal%20repeating%20texture%2C%20subtle%20design%20elements%2C%20clean%20background%20pattern&width=1200&height=800&seq=cta-bg-pattern&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-950 mb-3 md:mb-4" style={{ fontFamily: "var(--font-heading)" }}>{t("home.cta_title")}</h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6 md:mb-8">{t("home.cta_subtitle")}</p>
              <button className="text-sm md:text-base text-background-50 px-10 py-3.5 rounded-md font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90" style={{ backgroundColor: "oklch(var(--accent-500))" }}>
                {t("home.cta_button")}
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}